import React from 'react';
import {
    Button, Spinner, FormGroup, Row, Col, Container,
} from 'reactstrap';
import { groupBy, isEqual } from 'lodash-es';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    FormCheckbox, FormLabel, Input, toasty,
} from '../common/forms/FormElements';
import { ApiRoutes, isValidGuid } from '../Common';
import FileUpload from '../common/forms/FileUpload';
import { ValidatedSelect } from '../common/forms/ValidatedSelect';

const yesNoOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
];

const getDetailFieldId = (d) => d.customFormFieldId ?? d.customFormField?.id;

export const isRequiredWaived = (configurations, siblingDetails) => {
    if (!configurations?.length) return false;
    const groups = {};
    for (const c of configurations) {
        if (!c.requiredWaiverFieldId) continue;
        if (!groups[c.groupId]) {
            groups[c.groupId] = [];
        }
        groups[c.groupId].push(c);
    }
    return Object.values(groups).some((conditions) =>
        conditions.every((c) => {
            const sibling = siblingDetails.find((d) => getDetailFieldId(d) === c.requiredWaiverFieldId);
            return sibling?.value === c.requiredWaiverValue;
        }),
    );
};

export const resolveFieldIsRequired = (field, detail, siblingDetails, controlValues = {}) => {
    const controlProp = field.isRequiredControl ?? detail.isRequiredControl;
    const required = (controlProp && Object.hasOwn(controlValues, controlProp))
        ? !!controlValues[controlProp]
        : (field.isRequired ?? detail.isRequired ?? false);
    if (!required) return false;
    return !isRequiredWaived(field.configurations ?? [], siblingDetails);
};

const onFileDownload = async (file) => {
    if (file?.id) {
        const url = ApiRoutes.fileUpload.getAttachmentById(file.id);
        await httpClient.open(url, '_blank');
    }
};

class CustomFormComponent extends React.Component {
    constructor(props) {
        super(props);
        const details = props.formInstance?.details ?? [];
        this.state = {
            formInstance: props.formInstance,
            originalDetails: details,
            currentDetails: details,
            ocrLoadingFields: {},
            deleting: false,
            downloading: false,
        };
    }

    getFormData() {
        const { formInstance, currentDetails } = this.state;
        return {
            id: formInstance?.id ?? null,
            customFormId: formInstance?.customFormId,
            details: currentDetails,
        };
    }

    isDirty = () => !isEqual(this.state.currentDetails, this.state.originalDetails);

    handleFieldChange = (customFormFieldId, value, fieldType) => {
        const storedValue = fieldType === 'datetime-local' && value
            ? moment(value).format()
            : value;
        this.setState((prev) => ({
            currentDetails: prev.currentDetails.map((d) =>
                d.customFormFieldId === customFormFieldId ? { ...d, value: storedValue } : d,
            ),
        }));
    };

    handleAttachmentUploaded = (customFormFieldId, attachmentDto) => {
        this.setState((prev) => ({
            currentDetails: prev.currentDetails.map((d) =>
                d.customFormFieldId === customFormFieldId
                    ? { ...d, attachmentId: attachmentDto.id, attachmentFileName: attachmentDto.name }
                    : d,
            ),
        }));
    };

    handleDownload = async (file) => {
        if (!file?.id) return;
        this.setState({ downloading: true });
        try {
            await onFileDownload(file);
        } finally {
            this.setState({ downloading: false });
        }
    };

    handleAttachmentDeleted = (customFormFieldId) => {
        this.setState((prev) => ({
            currentDetails: prev.currentDetails.map((d) =>
                d.customFormFieldId === customFormFieldId
                    ? { ...d, attachmentId: null, attachmentFileName: null }
                    : d,
            ),
        }));
    };

    handleOcr = async (field) => {
        const { formInstance } = this.state;
        const fieldId = field.customFormFieldId ?? field.customFormField?.id;
        this.setState((prev) => ({
            ocrLoadingFields: { ...prev.ocrLoadingFields, [fieldId]: true },
        }));
        try {
            const updated = await httpClient.post(ApiRoutes.customForm.processOcr(), {
                attachmentId: field.attachmentId,
                formInstanceId: formInstance.id ?? null,
                customFormId: formInstance.customFormId,
            });
            if (!updated?.details) {
                toasty.error('Could not analyze form.');
                return;
            }
            this.setState((prev) => ({
                currentDetails: prev.currentDetails.map((d) => {
                    const ocrDetail = updated.details.find(
                        (od) => od.customFormFieldId === d.customFormFieldId,
                    );
                    const ocrKey = ocrDetail?.customFormField?.ocrFieldKey
                        ?? ocrDetail?.ocrFieldKey;
                    if (!ocrKey) return d;
                    return { ...d, value: ocrDetail.value };
                }),
            }));
            toasty.success('Form fields filled.');
        } catch (err) {
            toasty.error(err?.handledError?.message ?? 'Could not analyze form.');
        } finally {
            this.setState((prev) => ({
                ocrLoadingFields: { ...prev.ocrLoadingFields, [fieldId]: false },
            }));
        }
    };

    handleCancel = () => {
        this.setState((prev) => ({ currentDetails: prev.originalDetails }));
    };

    handleDeleteForm = async () => {
        const { onDeleteFormInstance } = this.props;
        const { formInstance, deleting } = this.state;
        const instanceId = formInstance?.id;

        if (!onDeleteFormInstance || !isValidGuid(instanceId) || deleting) return;

        const confirmed = window.confirm('Delete this form?');
        if (!confirmed) return;

        this.setState({ deleting: true });
        try {
            await onDeleteFormInstance(instanceId);
        } finally {
            this.setState({ deleting: false });
        }
    };

    renderField(field, {
        fieldId, fieldType, fieldLabel, fieldIsRequired, fieldIsOcrSource, fieldAcceptedFileTypes,
    }) {
        const { readOnly = false } = this.props;
        const { ocrLoadingFields } = this.state;

        switch (fieldType) {
            case 'message':
                return (
                    <FormGroup>
                        <FormLabel
                            className="font-weight-bold"
                            label={fieldLabel}
                        />
                    </FormGroup>
                );

            case 'warning':
                return (
                    <FormGroup>
                        <FormLabel
                            className="font-weight-bold text-warning mt-3"
                            label={fieldLabel}
                        />
                    </FormGroup>
                );

            case 'yes-no':
                return (
                    <ValidatedSelect
                        id={`field-${fieldId}`}
                        label={fieldLabel}
                        required={fieldIsRequired}
                        options={yesNoOptions}
                        value={yesNoOptions.find((o) => o.value === field.value) || null}
                        onChange={(selected) => this.handleFieldChange(fieldId, selected?.value ?? '', fieldType)}
                        isDisabled={readOnly}
                    />
                );

            case 'checkbox':
                return (
                    <FormCheckbox
                        id={`field-${fieldId}`}
                        labelText={fieldLabel}
                        checked={field.value === 'true'}
                        disabled={readOnly}
                        onChange={(e) => this.handleFieldChange(fieldId, e.target.checked ? 'true' : '', fieldType)}
                    />
                );

            case 'attachment':
                return (
                    <FormGroup>
                        <FormLabel
                            className={fieldIsRequired ? 'required' : ''}
                            label={fieldLabel}
                            htmlFor={`field-attachment-${fieldId}`}
                        />
                        {this.props.formValidated && fieldIsRequired && !field.attachmentId && (
                            <small className="text-danger">{fieldLabel} is required.</small>
                        )}
                        <FileUpload
                            key={field.attachmentId ?? `no-attachment-${fieldId}`}
                            id={`field-attachment-${fieldId}`}
                            url={ApiRoutes.fileUpload.createAttachment()}
                            {...(fieldAcceptedFileTypes ? { accept: fieldAcceptedFileTypes } : {})}
                            files={
                                field.attachmentId
                                    ? [{ id: field.attachmentId, name: field.attachmentFileName }]
                                    : []
                            }
                            type="simple"
                            buttonText={field.attachmentFileName ? 'Replace' : 'Upload'}
                            onUploadComplete={(attachmentDto) => this.handleAttachmentUploaded(fieldId, attachmentDto)}
                            onDelete={() => this.handleAttachmentDeleted(fieldId)}
                            onDownload={this.handleDownload}
                        />
                        {fieldIsOcrSource && field.attachmentId && (
                            <Button
                                type="button"
                                color="primary"
                                size="sm"
                                className="mt-3"
                                title="Try to fill form fields with file content"
                                disabled={ocrLoadingFields[fieldId]}
                                onClick={() => this.handleOcr(field)}
                            >
                                {ocrLoadingFields[fieldId]
                                    ? <Spinner size="sm" />
                                    : 'Fill Form from File'}
                            </Button>
                        )}
                    </FormGroup>
                );

            default:
                return (
                    <Input
                        type={fieldType}
                        label={fieldLabel}
                        value={fieldType === 'datetime-local' && field.value
                            ? moment(field.value).format('YYYY-MM-DDTHH:mm')
                            : (field.value ?? '')}
                        required={fieldIsRequired}
                        error={`${fieldLabel} is required.`}
                        onChange={(e) => this.handleFieldChange(fieldId, e.target.value, fieldType)}
                        disabled={readOnly}
                    />
                );
        }
    }

    render() {
        const { readOnly = false } = this.props;
        const {
            currentDetails,
            originalDetails,
            formInstance,
            deleting,
            downloading,
        } = this.state;

        if (!currentDetails) return null;

        const isDirty = !isEqual(currentDetails, originalDetails);
        const getField = (detail) => detail.customFormField ?? {};
        const getFieldId = (detail) => detail.customFormFieldId ?? getField(detail).id;
        const getFieldOrder = (detail) => getField(detail).fieldOrder ?? detail.fieldOrder ?? 0;
        const getRowIndex = (detail) => getField(detail).rowIndex ?? detail.rowIndex ?? 1;
        const getSpan = (detail) => getField(detail).span ?? detail.span ?? 12;
        const getFieldType = (detail) => getField(detail).fieldType ?? detail.fieldType;
        const getFieldLabel = (detail) => getField(detail).label ?? detail.fieldLabel;
        const isRequired = (detail) => resolveFieldIsRequired(
            getField(detail), detail, currentDetails, this.props.controlValues,
        );
        const isOcrSource = (detail) => getField(detail).isOCRSource ?? detail.isOCRSource ?? false;
        const getAcceptedFileTypes = (detail) => getField(detail).acceptedFileTypes ?? detail.acceptedFileTypes ?? null;
        const getIsRequiredToComplete = (detail) => getField(detail).isRequiredToComplete ?? detail.isRequiredToComplete ?? false;
        const rows = groupBy(currentDetails, (d) => getRowIndex(d));
        const sortedRowKeys = Object.keys(rows).sort((a, b) => +a - +b);

        return (
            <Container
                fluid
                className="pb-2 mb-3"
                style={downloading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
                {currentDetails.some((d) => getIsRequiredToComplete(d)) && (
                    <small className="text-muted d-block mb-2">* Required to complete</small>
                )}
                {downloading && (
                    <div className="text-center mb-2">
                        <Spinner size="sm" />
                        {' '}
                        Downloading...
                    </div>
                )}
                {sortedRowKeys.map((rowIdx) => {
                    const rowFields = [...rows[rowIdx]].sort((a, b) => getFieldOrder(a) - getFieldOrder(b));
                    return (
                        <Row
                            className="mb-2"
                            key={rowIdx}
                        >
                            {rowFields.map((field) => {
                                const fieldId = getFieldId(field);
                                const fieldType = getFieldType(field);
                                const fieldIsRequiredToComplete = getIsRequiredToComplete(field);
                                const fieldLabel = fieldIsRequiredToComplete ? `${getFieldLabel(field)}*` : getFieldLabel(field);
                                const fieldSpan = getSpan(field);
                                const fieldIsRequired = isRequired(field);
                                const fieldIsOcrSource = isOcrSource(field);
                                const fieldAcceptedFileTypes = getAcceptedFileTypes(field);

                                return (
                                    <Col
                                        xs={12}
                                        lg={fieldSpan}
                                        key={fieldId}
                                    >
                                        {this.renderField(field, {
                                            fieldId,
                                            fieldType,
                                            fieldLabel,
                                            fieldIsRequired,
                                            fieldIsOcrSource,
                                            fieldAcceptedFileTypes,
                                            fieldIsRequiredToComplete,
                                        })}
                                    </Col>
                                );
                            })}
                        </Row>
                    );
                })}

                {!readOnly && isDirty && (
                    <Row>
                        <Col className="d-flex justify-content-start mt-2">
                            <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                onClick={this.handleCancel}
                            >
                                <FontAwesomeIcon
                                    className="mr-1"
                                    icon={faTimes}
                                />
                                Cancel Changes
                            </Button>
                        </Col>
                    </Row>
                )}

                {!readOnly && !!this.props.onDeleteFormInstance && isValidGuid(formInstance?.id) && (
                    <Row>
                        <Col className="d-flex justify-content-start mt-2">
                            <Button
                                type="button"
                                color="danger"
                                size="sm"
                                outline
                                disabled={deleting}
                                onClick={this.handleDeleteForm}
                            >
                                <FontAwesomeIcon
                                    className="mr-1"
                                    icon={faTrashAlt}
                                />
                                {deleting ? 'Deleting...' : 'Delete Form'}
                            </Button>
                        </Col>
                    </Row>
                )}
            </Container>
        );
    }
}

export default CustomFormComponent;
